import { Component, DestroyRef, effect, inject, signal } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { WhiteLabelService } from '@crewdle/white-label/angular';
import { partnerIdForOrg } from '@crewdle/white-label';
import { environment } from '../environments/environment';
import { FirebaseService } from './core/services/firebase.service';
import { OrgContextService } from './core/services/org-context.service';
import { ThemeService } from './core/theme.service';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet],
  templateUrl: './app.html',
  styleUrl: './app.css',
})
export class App {
  // Gates the `<router-outlet>` behind a full-page loader until white-label
  // branding has settled — otherwise the app can paint with Crewdle defaults
  // for a frame before a partner brand replaces it.
  readonly booting = signal(true);

  constructor() {
    // Previously only instantiated by the sidebar (mounted well after this
    // boot loader would already be gone), so the `dark` class never landed
    // on <html> in time — the loader always painted in light-mode colors
    // regardless of system/saved preference. Injecting it here runs its
    // constructor effect immediately, before the loader's first paint.
    inject(ThemeService);

    const whiteLabel = inject(WhiteLabelService);
    const orgContext = inject(OrgContextService);
    effect(() => whiteLabel.setPartnerId(partnerIdForOrg(orgContext.currentOrg())));
    whiteLabel.settled().then(() => this.booting.set(false));

    this.setupTokenRefreshOnForeground();
  }

  /**
   * A tab backgrounded past the Firebase ID token's ~1h lifetime resumes its
   * Firestore listeners with a stale token on return to the foreground. The
   * SDK's own background refresh timer can be delayed by browser throttling
   * of inactive tabs, so listeners can reject with `unauthenticated` before
   * it catches up — surfacing as an unhandled observer error (see the error
   * handlers added alongside this to `OrgContextService` and
   * `ChatComponent`). Forcing a refresh as soon as the tab is foregrounded
   * again closes that window.
   */
  private setupTokenRefreshOnForeground(): void {
    const firebase = inject(FirebaseService);
    const refresh = () => {
      void firebase.auth.currentUser?.getIdToken(true).catch(() => {
        // A failed forced refresh (e.g. offline) leaves the SDK's own retry
        // logic to recover; nothing else to do here.
      });
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') refresh();
    };

    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', onVisibility);
    inject(DestroyRef).onDestroy(() => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', onVisibility);
    });
  }
}
